import { useCallback, useRef, useEffect } from 'react';

interface AnalyticsEvent {
  event_type: string;
  event_action: string;
  standard_slug?: string;
  metadata?: Record<string, unknown>;
}

// Session ID management
function getSessionId(): string {
  const key = 'aapi_session_id';
  let sessionId = sessionStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }
  return sessionId;
}

// Batched event queue
const eventQueue: AnalyticsEvent[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_BATCH_SIZE = 10;

async function flushEvents() {
  if (eventQueue.length === 0) return;

  const eventsToSend = eventQueue.splice(0, MAX_BATCH_SIZE);
  const sessionId = getSessionId();

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-event`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          events: eventsToSend,
          session_id: sessionId,
        }),
      }
    );

    if (!response.ok && response.status !== 429) {
      console.warn('Analytics tracking failed:', response.status);
    }
  } catch (error) {
    // Silent fail for analytics
    console.warn('Analytics tracking error:', error);
  }

  // If there are more events, schedule another flush
  if (eventQueue.length > 0) {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimeout) return;
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushEvents();
  }, FLUSH_INTERVAL);
}

function queueEvent(event: AnalyticsEvent) {
  eventQueue.push(event);
  
  // Flush immediately if we hit max batch size
  if (eventQueue.length >= MAX_BATCH_SIZE) {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
      flushTimeout = null;
    }
    flushEvents();
  } else {
    scheduleFlush();
  }
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (eventQueue.length > 0) {
      const sessionId = getSessionId();
      // Use sendBeacon for reliable delivery on unload
      navigator.sendBeacon(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-event`,
        JSON.stringify({
          events: eventQueue.splice(0, MAX_BATCH_SIZE),
          session_id: sessionId,
        })
      );
    }
  });
}

export function useAnalytics() {
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTrackedRef = useRef<{ standard?: string; search?: string; adfElement?: string; doc?: string }>({});

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const trackPageView = useCallback((path: string) => {
    queueEvent({
      event_type: 'page_view',
      event_action: 'view',
      metadata: { path },
    });
  }, []);

  const trackStandardView = useCallback((slug: string) => {
    // Deduplicate rapid views of the same standard
    if (lastTrackedRef.current.standard === slug) return;
    lastTrackedRef.current.standard = slug;

    queueEvent({
      event_type: 'standard',
      event_action: 'view',
      standard_slug: slug,
    });
  }, []);

  const trackExport = useCallback((slug: string, format: string) => {
    queueEvent({
      event_type: 'standard',
      event_action: 'export',
      standard_slug: slug,
      metadata: { format },
    });
  }, []);

  const trackValidation = useCallback((slug: string, valid: boolean) => {
    queueEvent({
      event_type: 'standard',
      event_action: 'validate',
      standard_slug: slug,
      metadata: { valid },
    });
  }, []);

  const trackBuilderAction = useCallback((action: string, metadata: Record<string, unknown>) => {
    queueEvent({
      event_type: 'builder',
      event_action: action,
      metadata,
    });
  }, []);

  const trackSearch = useCallback((term: string, resultCount: number) => {
    // Debounce search tracking - wait 1 second after typing stops
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      // Don't track if same search term
      if (lastTrackedRef.current.search === term) return;
      lastTrackedRef.current.search = term;

      queueEvent({
        event_type: 'search',
        event_action: 'query',
        metadata: { term: term.slice(0, 100), resultCount },
      });
    }, 1000);
  }, []);

  // New: Track ADF element views
  const trackAdfElementView = useCallback((elementSlug: string) => {
    // Deduplicate rapid views of the same element
    if (lastTrackedRef.current.adfElement === elementSlug) return;
    lastTrackedRef.current.adfElement = elementSlug;

    queueEvent({
      event_type: 'adf',
      event_action: 'view',
      metadata: { element_slug: elementSlug },
    });
  }, []);

  // New: Track ADF validation
  const trackAdfValidation = useCallback((valid: boolean, errorCount: number) => {
    queueEvent({
      event_type: 'adf',
      event_action: 'validate',
      metadata: { valid, errorCount },
    });
  }, []);

  // New: Track ADF export
  const trackAdfExport = useCallback((format: 'xml' | 'xsd' | 'csv', action: 'download' | 'copy') => {
    queueEvent({
      event_type: 'adf',
      event_action: 'export',
      metadata: { format, action },
    });
  }, []);

  // New: Track ADF builder actions
  const trackAdfBuilderAction = useCallback((action: string, metadata: Record<string, unknown>) => {
    queueEvent({
      event_type: 'adf_builder',
      event_action: action,
      metadata,
    });
  }, []);

  // New: Track documentation views
  const trackDocView = useCallback((docType: 'guide' | 'field' | 'datatype' | 'lookup', slug: string) => {
    const key = `${docType}:${slug}`;
    // Deduplicate rapid views of the same doc
    if (lastTrackedRef.current.doc === key) return;
    lastTrackedRef.current.doc = key;

    queueEvent({
      event_type: 'documentation',
      event_action: 'view',
      metadata: { docType, slug },
    });
  }, []);

  return {
    trackPageView,
    trackStandardView,
    trackExport,
    trackValidation,
    trackBuilderAction,
    trackSearch,
    // New tracking functions
    trackAdfElementView,
    trackAdfValidation,
    trackAdfExport,
    trackAdfBuilderAction,
    trackDocView,
  };
}
